/*
 * MULTI-SELECT.JSX - Reusable Multi-Select Component
 * 
 * SUMMARY:
 * This component is a wrapper around the 'react-select' library to provide a customizable and consistent
 * multi-select (and single-select) dropdown across the application. It supports features like grouped options,
 * custom styling, validation, and special option rendering for hierarchical data.
 * 
 * FEATURES:
 * 1. Single and Multi-select functionality.
 * 2. Custom styling to match the application's theme.
 * 3. Support for grouped options with group labels and item counts.
 * 4. Special 'stage' style option rendering for hierarchical data (e.g., 'Parent > Child').
 * 5. Built-in handling for required fields and validation messages.
 * 6. Searchable and non-searchable modes.
 */
/*
 * MULTI-SELECT.JSX - Reusable Multi-Select Component
 * 
 * SUMMARY:
 * This component is a wrapper around the 'react-select' library to provide a customizable and consistent
 * multi-select (and single-select) dropdown across the application. It supports features like grouped options,
 * custom styling, validation, and special option rendering for hierarchical data.
 * 
 * FEATURES:
 * 1. Single and Multi-select functionality.
 * 2. Custom styling to match the application's theme.
 * 3. Support for grouped options with group labels and item counts.
 * 4. Special 'stage' style option rendering for hierarchical data (e.g., 'Parent > Child').
 * 5. Built-in handling for required fields and validation messages.
 * 6. Searchable and non-searchable modes.
 */
import React from 'react';
import Select, { components } from 'react-select';
import _ from 'lodash';
import { DefaultSectionOptions } from '../../util/enums';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import './multi-select.scss';

/** Indentation in px per nesting level for hierarchical display */
const TREE_INDENT_PX = 20;

/**
 * Custom Option component with tree structure formatting (for isStage = true).
 * Uses indentation only (no tree-line characters) for a clean hierarchy.
 * Falls back to legacy ">" path display if depth information is not available.
 */
const Option = ({ children, ...props }) => {
    const { depth, displayName, isSectionHeader, isStage, isAttributionLevel, is_attribution_level } = props.data;

    // Check if we have tree structure metadata
    const hasTreeMetadata = typeof depth === 'number';
    const showIcon = !!(isAttributionLevel || is_attribution_level);

    if (hasTreeMetadata) {
        const itemName = displayName || props.data.label || String(children);
        const indentPx = depth * TREE_INDENT_PX;
        const isBold = isSectionHeader || isStage;
        const textColor = isSectionHeader ? '#888' : 'inherit';

        return (
            <components.Option {...props} key={props.data.value}>
                <div
                    data-cy={`option-${props.data.value}`}
                    data-attribution={showIcon ? "true" : "false"}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        paddingLeft: indentPx,
                        gap: '8px'
                    }}
                >
                    {showIcon && (
                        <AssignmentTurnedInIcon
                            fontSize="small"
                            color={props.isSelected ? "inherit" : "primary"}
                        />
                    )}
                    <span
                        style={{
                            fontWeight: isBold ? 'bold' : 'normal',
                            color: textColor
                        }}
                    >
                        {itemName}
                    </span>
                </div>
            </components.Option>
        );
    }

    // Fallback: Legacy display without tree metadata (for backward compatibility)
    const childText = typeof children === 'string' ? children : String(children);
    const parts = childText.split(' > ');
    const lastPart = parts.pop();
    const restOfPath = parts.join(' > ');

    return (
        <components.Option {...props} key={props.data.value}>
            <div
                data-cy={`option-${props.data.value}`}
                data-attribution={showIcon ? "true" : "false"}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
                {showIcon && (
                    <AssignmentTurnedInIcon
                        fontSize="small"
                        color={props.isSelected ? "inherit" : "primary"}
                    />
                )}
                <div>
                    <span>{restOfPath ? restOfPath + ' > ' : ''}</span>
                    <strong className='section-dropdown-lastpart'>{lastPart}</strong>
                </div>
            </div>
        </components.Option>
    );
};

// Normal Option component without special formatting (for isStage = false)
const NormalOption = ({ children, ...props }) => {
    const { isAttributionLevel, is_attribution_level } = props.data;
    const showIcon = !!(isAttributionLevel || is_attribution_level);
    return (
        <components.Option {...props} key={props.data.value}>
            {/* Moved data-cy to the inner div to ensure it appears */}
            <div
                data-cy={`option`}
                data-cy-value={`${props.data.label || props.data.value}`}
                data-attribution={showIcon ? "true" : "false"}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
                {showIcon && (
                    <AssignmentTurnedInIcon
                        fontSize="small"
                        color={props.isSelected ? "inherit" : "primary"}
                    />
                )}
                {children}
            </div>
        </components.Option>
    );
};

// New custom Menu component to add data-cy to the menu itself
const Menu = (props) => {
    return (
        <components.Menu {...props}>
            {/* Applied data-cy to an inner div for the menu as well for consistency and reliability */}
            <div data-cy="select-menu-wrapper">
                {props.children}
            </div>
        </components.Menu>
    );
};

// New custom ValueContainer to add data-cy to the selected input
const CustomValueContainer = ({ children, ...props }) => {
    let dataCyValue = '';
    const selectedOptions = props.getValue(); // Get the array of selected options

    // data-cy="select-input-none"
    if (props.isMulti) {
        // For multi-select, join the values with a comma
        dataCyValue = selectedOptions.map(opt => opt.label || opt.value).join(',') || 'no-selection';
    } else if (selectedOptions.length > 0) {
        // For single select, use the value of the first selected option
        dataCyValue = selectedOptions[0].label || selectedOptions[0].value || 'no-selection';
    } else {
        // No option selected, use a default string or empty
        dataCyValue = 'no-selection';
    }
    return (
        <components.ValueContainer {...props}>
            <div data-cy={`select-input`} data-cy-value={dataCyValue} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
                {children}
            </div>
        </components.ValueContainer>
    );
};


export default function MultiSelect({
    label = '',
    placeholder = '',
    validation = [],
    required,
    className = '',
    isStage = false,
    isMulti = false,
    onChangeHandler,
    isSearchable = true,
    setDefaultOption = false,
    dropdownWidth = '100%',
    'data-cy': dataCy, ...props
}) {
    let isRequired = required || (validation.includes('required') && !(validation.includes('skip') || validation.includes('optional')));
    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            height: 'auto',
            minHeight: '45px',
            borderColor: '#e2e2e2',
            borderRadius: '8px',
            ...(state.isFocused && {
                borderColor: '#86b7fe',
                outline: '0',
                boxShadow: '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
            }),
        }),
        placeholder: (provided) => ({
            ...provided,
            textAlign: 'left'
        }),
        option: (provided, state) => ({
            ...provided,
            textAlign: 'left',
            backgroundColor: state.isSelected ? 'var(--brand-color-3)' : provided.backgroundColor,
            color: state.isSelected ? 'white' : provided.color,
        }),
        menu: (provided) => ({
            ...provided,
            width: dropdownWidth,
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 2147483647,
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: 'var(--brand-color-4)',
            color: 'white',
            borderRadius: '4px',
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: 'white',
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: 'white',
            ':hover': {
                backgroundColor: 'var(--brand-color-3)',
                color: 'white',
            },
        }),
        valueContainer: (provided) => ({
            ...provided,
            maxHeight: '100px',
            overflowY: 'auto',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            color: 'var(--brand-color-3)',
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            // Optional: style for the separator if needed
        }),
    };

    const defaultOption = setDefaultOption ? [{ label: DefaultSectionOptions.LABEL, value: DefaultSectionOptions.VALUE }] : [];
    // Ensure props.items is an array before spreading
    const options = [...defaultOption, ...(Array.isArray(props.items) ? props.items : [])];

    const formatGroupLabel = data => (
        <div className="group-styles">
            <span>{data.label}</span>
            {!isStage && <span className="group-badge">{data.options.length}</span>}
        </div>
    );

    return (
        <div className="form-group w-100">
            <div className={_.uniq(["me-3 input-block", ...className.split(' ')]).join(' ')}>
                {label && <label className="input-label">
                    <span>{label || ''} {isRequired ? <span className='text-danger'>*</span> : null}</span>
                </label>}
                <div data-cy={`${dataCy}`}>
                    <Select
                        isMulti={isMulti}
                        options={options}
                        value={props.value}
                        onChange={onChangeHandler}
                        styles={customStyles}
                        placeholder={placeholder || label || 'Select an option'}
                        formatGroupLabel={formatGroupLabel}
                        components={{
                            // Conditionally apply Option or NormalOption based on isStage
                            Option: isStage ? Option : NormalOption,
                            // Always apply the custom Menu component
                            Menu,
                            // Use the custom ValueContainer to add data-cy to the input area
                            ValueContainer: CustomValueContainer,
                        }}
                        isSearchable={isSearchable}
                        noOptionsMessage={() => "No options available"}
                        menuPortalTarget={document.body}
                    />
                </div>
                {props.error && props.touched && <small className="float-start text-danger">
                    {props.error}
                </small>}
            </div>
        </div>
    );
}